<template>
    <div class="flex flex-column align-items-center min-h-screen w-full pt-6">

        <!-- Loading packages -->
        <div v-if="packagesLoading" class="flex align-items-center justify-content-center py-8 gap-3">
            <ProgressSpinner style="width: 40px; height: 40px" />
            <span class="text-600">Loading packages...</span>
        </div>

        <SubscriptionPlans
            v-else
            mode="onboarding"
            :packages="studentPackages"
            :featureDefinitions="studentFeatures"
            :disableActions="actionLoading"
            :currentPlanId="packageActivated ? selectedPackage?.id : null"
            @package-action="handlePackageAction"
        />

        <!-- Billing Details Dialog -->
        <BillingDetailsDialog
            v-model="showBillingDialog"
            :context="billingContext"
            :selected-package="selectedPackage"
            :initial-values="billingInitialValues"
            :grade-pricing="selectedPackage?.pricingType === 'grade_based' ? selectedPackage?.priceBreakdown : null"
            :preselected-grade="billingPreselectedGrade"
            @submit="submitBilling"
        />

        <!-- Progress Dialog -->
        <Dialog
            v-model:visible="showProgressDialog"
            modal
            :closable="false"
            header="Setting up your subscription"
            :style="{ width: 'min(420px, 92vw)' }"
        >
            <div class="flex flex-column align-items-center py-4 gap-4">
                <ProgressSpinner style="width: 50px; height: 50px" />
                <p class="text-600 text-center m-0">{{ progressMessage }}</p>
            </div>
        </Dialog>


    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue';
import SubscriptionPlans from '@/components/subscriptionPlans/studentSubscriptionPlans.vue';
import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/authStore';
import payments from '@/api/payments';
import subscriptionApi from '@/api/subscription';
import { updateProfile } from '@/api/student';
import { backendApi } from '@/api/axios';
import { PACKAGES_ENDPOINTS, PAYMENT_ENDPOINTS } from '@/api/endpoints';
import { notifyError, notifyContrast } from '@/utils/notification';
import { notifyIfManagedByInstitute } from '@/utils/subscriptionGuards';

const PENDING_KEY = 'studentOnboardingPendingPackage';

const emit = defineEmits(['package-selected']);
const authStore = useAuthStore();

// UI state
const packagesLoading = ref(true);
const actionLoading   = ref(false);

const showBillingDialog = ref(false);
const billingContext    = ref('free_tokenize');

const showProgressDialog = ref(false);
const progressMessage    = ref('');
const packageActivated   = ref(false);

// Data
const studentPackages  = ref([]);
const selectedPackage  = ref(null);
const suggestedGradeKey = ref(null);

// Grade object matching the student's education level — pre-fills billing dialog grade dropdown
const billingPreselectedGrade = computed(() => {
    if (!suggestedGradeKey.value || !selectedPackage.value?.priceBreakdown) return null;
    return selectedPackage.value.priceBreakdown.find(g => g.gradeKey === suggestedGradeKey.value) || null;
});

const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName    || '',
        lastName:  u.lastName     || '',
        email:     u.email        || '',
        phone:     u.mobileNumber || '',
        address:   u.address      || '',
        city:      u.city         || '',
    };
});

const studentFeatures = ref([
    { key: 'sessions',   label: 'Maximum tutors' },
    { key: 'notes',      label: 'Maximum notes' },
    { key: 'payments',   label: 'Secure payments' },
    { key: 'chat',       label: 'In app chat' },
    { key: 'pastPapers', label: 'Past paper access' },
    { key: 'aiFeatures', label: 'AI Study Assistant', info: 'Get access to our AI learning assistant' },
]);

function mapLimitValue(value) {
    return value === null || value === undefined ? 'Unlimited' : value.toString();
}

function mapStringFeature(value) {
    if (value === null || value === false || value === 'none') return false;
    // 'all' means the feature is fully included - show a tick rather than the word "All".
    if (value === true || value === 'all') return true;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return value;
}

function mapApiPackage(pkg) {
    const isGradeBased = pkg.pricing.type === 'grade_based';
    const isFree       = pkg.pricing.type === 'free';

    return {
        id:          pkg._id,
        name:        pkg.name,
        priceLabel:  isGradeBased ? 'Based on Grade' : (isFree ? '0' : pkg.pricing.amount.toString()),
        popular:     pkg.isPopular,
        pricingType: pkg.pricing.type,
        subtitle:    isFree ? 'Free for all Students' : undefined,
        priceBreakdown: isGradeBased && pkg.pricing.gradePricing?.length
            ? pkg.pricing.gradePricing.map(g => ({ gradeKey: g.gradeKey, label: g.label, amount: g.amount, price: g.amount.toString() }))
            : undefined,
        features: {
            sessions:   mapLimitValue(pkg.limits?.bookSessionsMaxTutors),
            notes:      mapLimitValue(pkg.limits?.maximumNotes),
            payments:   pkg.features?.securePayments ?? true,
            chat:       pkg.features?.inAppChat ?? true,
            pastPapers: pkg.features?.pastPaperAccess ?? false,
            aiFeatures: mapStringFeature(pkg.features?.aiFeatures ?? false),
        },
    };
}

function markActivated(pkg) {
    if (pkg) selectedPackage.value = pkg;
    packageActivated.value = true;
}

function findPackageFromSub(sub) {
    const rawId = sub.packageId?._id ?? sub.packageId;
    if (!rawId) return null;
    return studentPackages.value.find(p => String(p.id) === String(rawId)) ?? null;
}

async function fetchPackages() {
    try {
        const res = await backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'student' } });
        studentPackages.value = (res.data?.data || []).map(mapApiPackage);
    } catch {
        notifyError('Error', 'Failed to load packages. Please refresh the page.');
    }
}

async function checkHasToken() {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
}

async function waitForSubscriptionActive(maxAttempts = 15, intervalMs = 2000) {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const res = await subscriptionApi.getMy();
            if (res?.data?.data?.subscription?.status === 'active') return true;
        } catch { /* ignore transient errors */ }
        await new Promise(r => setTimeout(r, intervalMs));
    }
    return false;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

async function doSubscribe(packageId, billingData) {
    progressMessage.value    = 'Setting up your subscription...';
    showProgressDialog.value = true;
    try {
        const payload = { packageId, ...(billingData || {}) };
        const res  = await subscriptionApi.subscribe(payload);
        const data = res?.data?.data;

        if (data?.requiresCheckout) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'paid', ts: Date.now() }));
            redirectToPayHere(data.checkoutData, true);
            return;
        }

        if (data?.requiresTokenization) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'free', ts: Date.now() }));
            redirectToPayHere(data.tokenizationData, true);
            return;
        }

        if (data?.status === 'active') {
            showProgressDialog.value = false;
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            emit('package-selected', { ...selectedPackage.value });
            return;
        }

        progressMessage.value = 'Waiting for subscription to activate...';
        const activated = await waitForSubscriptionActive();
        showProgressDialog.value = false;
        if (activated) {
            sessionStorage.removeItem(PENDING_KEY);
            markActivated(selectedPackage.value);
            notifyContrast('Success', 'Your subscription is now active!');
            emit('package-selected', { ...selectedPackage.value });
        } else {
            notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
        }
    } catch (err) {
        showProgressDialog.value = false;
        if (notifyIfManagedByInstitute(err)) return;
        const msg = err?.response?.data?.message || '';
        if (msg.toLowerCase().includes('already have an active subscription')) {
            await resolveExistingSubscription();
        } else {
            notifyError('Error', msg || 'Failed to subscribe. Please try again.');
        }
    }
}

async function handlePackageAction(event) {
    selectedPackage.value = event.package;
    actionLoading.value   = true;
    try {
        const hasToken = await checkHasToken();
        if (hasToken) {
            await doSubscribe(selectedPackage.value.id, null);
        } else {
            billingContext.value    = selectedPackage.value.pricingType === 'free'
                ? 'free_tokenize'
                : 'paid_subscribe';
            showBillingDialog.value = true;
        }
    } catch {
        notifyError('Error', 'Something went wrong. Please try again.');
    } finally {
        actionLoading.value = false;
    }
}

async function submitBilling(formData) {
    showBillingDialog.value = false;

    try {
        await updateProfile({
            firstName:    formData.firstName,
            lastName:     formData.lastName,
            mobileNumber: formData.phone,
            address:      formData.address,
            city:         formData.city,
        });
        if (authStore.user) {
            authStore.user.firstName    = formData.firstName;
            authStore.user.lastName     = formData.lastName;
            authStore.user.mobileNumber = formData.phone;
            authStore.user.address      = formData.address;
            authStore.user.city         = formData.city;
        }
    } catch { /* ignore */ }

    await doSubscribe(selectedPackage.value.id, formData);
}

async function checkPendingSubscription() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); } catch {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    if (!pending?.packageId) {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    // If PaymentSuccess set the success flag, poll for webhook activation.
    // Otherwise the payment was cancelled / "Back to site" — clean up, no popup.
    const isSuccess = !!sessionStorage.getItem('studentOnboardingPaymentSuccess');
    sessionStorage.removeItem('studentOnboardingPaymentSuccess');

    if (!isSuccess) {
        sessionStorage.removeItem(PENDING_KEY);
        subscriptionApi.cancelPending().catch(() => {});
        return;
    }

    const pkg = studentPackages.value.find(p => p.id === pending.packageId);
    if (pkg) selectedPackage.value = pkg;

    progressMessage.value    = 'Activating your subscription, please wait...';
    showProgressDialog.value = true;

    const activated = await waitForSubscriptionActive();
    showProgressDialog.value = false;
    sessionStorage.removeItem(PENDING_KEY);

    if (activated) {
        markActivated(selectedPackage.value);
        notifyContrast('Success', 'Your subscription is now active!');
        emit('package-selected', { ...selectedPackage.value });
    } else {
        notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
    }
}

async function resolveExistingSubscription() {
    try {
        const res = await subscriptionApi.getMy();
        const sub = res?.data?.data?.subscription;
        if (sub?.status === 'active') {
            const pkg = findPackageFromSub(sub);
            markActivated(pkg);
            notifyContrast('Subscription Active', 'You already have an active subscription.');
            emit('package-selected', pkg ? { ...pkg } : {});
        }
    } catch { /* silent */ }
}

onMounted(async () => {
    packagesLoading.value = true;
    try {
        await fetchPackages();

        const hasPending = !!sessionStorage.getItem(PENDING_KEY);

        try {
            const res  = await subscriptionApi.getMy();
            const data = res?.data?.data ?? {};
            suggestedGradeKey.value = data.suggestedGradeKey ?? null;
            const sub = data.subscription;
            if (sub?.status === 'active') {
                const pkg = findPackageFromSub(sub);
                markActivated(pkg);
                if (hasPending) {
                    // Returning from PayHere — subscription confirmed, finalize immediately
                    sessionStorage.removeItem(PENDING_KEY);
                    emit('package-selected', pkg ? { ...pkg } : {});
                }
                return;
            }
        } catch { /* no subscription yet */ }

        await checkPendingSubscription();
    } finally {
        packagesLoading.value = false;
    }
});
</script>
